import { useUserStore } from "@/stores/userStore";
import type { Team } from "@repo/types";

/**
 * There are two common use cases for these hooks when passed to a hook like useAgendaItemList:
 *
 * This form will return an empty list while loading, and also return an empty list if
 * 'full organization' is selected:
 *
 * ```ts
 * useAgendaItemList(tenantId, useActiveTeamId() || "");
 * ```
 *
 * This form will return an empty list while loading, but return all items when 'full
 * organization' is selected:
 *
 * ```ts
 * useAgendaItemList(tenantId, useActiveTeamIdOrOrg() ?? "");
 * ```
 */

/** Gets the active team object. If 'full organization' is selected, returns `false`. If team
 * details are still loading, returns `null`. */
export const useActiveTeam = (): Team | false | null => {
  const activeTeam = useUserStore((s) => s.activeTeam);
  if (activeTeam === "org") return false;
  return activeTeam ?? null;
};

/** Gets the active team's id. If 'full organization' is selected, returns `false`. If team
 * details haven't loaded, returns `null`. */
export const useActiveTeamId = (): string | false | null => {
  const activeTeam = useUserStore((s) => s.activeTeam);
  if (activeTeam === "org") return false;
  return activeTeam?.id ?? null;
};

/** Gets the active team's id, or "org" if 'full organization' is selected. If team details
 * haven't loaded, returns `null`. */
export const useActiveTeamIdOrOrg = (): string | null => {
  const activeTeam = useUserStore((s) => s.activeTeam);
  if (activeTeam === "org") return "org";
  return activeTeam?.id ?? null;
};
